import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Shield, Heart, Mail, Send } from 'lucide-react';

const modalContent = {
  termos: {
    title: 'Termos de Uso',
    icon: FileText,
    sections: [
      {
        title: '1. Aceitação dos Termos',
        text: 'Ao acessar e utilizar o aplicativo MenoEase ("Serviço"), você concorda com estes Termos de Uso. Caso não concorde com algum dos termos aqui descritos, solicitamos que não utilize o Serviço.',
      },
      {
        title: '2. Descrição do Serviço',
        text: 'O MenoEase é um aplicativo de acompanhamento e bem-estar voltado para mulheres na menopausa. O Serviço oferece funcionalidades como:',
        list: [
          'Rastreamento de sintomas e hábitos diários',
          'Monitoramento de ciclo menstrual',
          'Chat com inteligência artificial para dúvidas sobre menopausa',
          'Geração de relatórios de saúde',
          'Conteúdo educativo sobre saúde feminina',
        ],
      },
      {
        title: '3. Aviso Médico Importante',
        text: 'O MenoEase não substitui consultas médicas, diagnósticos ou tratamentos profissionais. As informações fornecidas pelo aplicativo, incluindo respostas do chat com IA, têm caráter exclusivamente informativo e educacional. Sempre consulte um profissional de saúde qualificado para decisões médicas.',
      },
      {
        title: '4. Cadastro e Conta',
        text: 'Para utilizar o Serviço, você deve criar uma conta fornecendo informações verdadeiras e atualizadas. Você é responsável por manter a confidencialidade de suas credenciais de acesso e por todas as atividades realizadas em sua conta.',
      },
      {
        title: '5. Planos e Pagamentos',
        text: 'O MenoEase oferece um plano gratuito com funcionalidades básicas e planos pagos (Mensal, Anual e Vitalício) com recursos adicionais. Os valores estão descritos na seção de Planos do aplicativo. Oferecemos garantia de reembolso de 7 dias para todos os planos pagos.',
      },
      {
        title: '6. Cancelamento',
        text: 'Você pode cancelar sua assinatura a qualquer momento, sem cobrança de taxas adicionais. O acesso aos recursos premium permanece ativo até o final do período já pago.',
      },
      {
        title: '7. Propriedade Intelectual',
        text: 'Todo o conteúdo do MenoEase — incluindo textos, imagens, design, código e marca — é protegido por direitos autorais e pertence ao MenoEase ou seus licenciadores. É proibida a reprodução, distribuição ou modificação sem autorização prévia.',
      },
      {
        title: '8. Limitação de Responsabilidade',
        text: 'O MenoEase não se responsabiliza por decisões tomadas com base nas informações fornecidas pelo aplicativo. O Serviço é fornecido "como está", sem garantias de que atenderá a necessidades médicas específicas.',
      },
      {
        title: '9. Alterações nos Termos',
        text: 'Reservamo-nos o direito de atualizar estes Termos periodicamente. Alterações significativas serão comunicadas por e-mail ou notificação no aplicativo. O uso continuado do Serviço após alterações constitui aceitação dos novos termos.',
      },
      {
        title: '10. Contato',
        text: 'Para dúvidas sobre estes Termos de Uso, entre em contato pelo e-mail: contato@menoease.com',
      },
    ],
  },
  privacidade: {
    title: 'Política de Privacidade',
    icon: Shield,
    intro: 'O MenoEase leva a privacidade dos seus dados a sério. Esta política explica como coletamos, usamos, armazenamos e protegemos suas informações pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).',
    sections: [
      {
        title: '1. Dados que Coletamos',
        text: 'Podemos coletar os seguintes tipos de informações:',
        list: [
          'Dados de cadastro: nome, e-mail, data de nascimento',
          'Dados de saúde: sintomas registrados, ciclo menstrual, qualidade de sono, humor, hábitos de hidratação e exercícios',
          'Dados de uso: interações com o app, funcionalidades utilizadas, horários de acesso',
          'Dados de pagamento: processados por provedores terceiros seguros (não armazenamos dados de cartão)',
        ],
      },
      {
        title: '2. Como Usamos seus Dados',
        list: [
          'Fornecer e personalizar o Serviço',
          'Gerar relatórios e insights sobre seus sintomas',
          'Alimentar o chat com IA para respostas personalizadas',
          'Enviar notificações e lembretes configurados por você',
          'Melhorar o aplicativo com base em dados agregados e anonimizados',
        ],
      },
      {
        title: '3. Dados Sensíveis de Saúde',
        text: 'Reconhecemos que seus dados de saúde são especialmente sensíveis. Por isso:',
        list: [
          'Seus dados de saúde são criptografados em trânsito e em repouso',
          'Nunca vendemos ou compartilhamos seus dados de saúde com terceiros',
          'O acesso interno é restrito a sistemas automatizados necessários para o funcionamento do app',
          'Você pode exportar ou excluir todos os seus dados a qualquer momento',
        ],
      },
      {
        title: '4. Compartilhamento de Dados',
        text: 'Não vendemos suas informações pessoais. Podemos compartilhar dados apenas com:',
        list: [
          'Provedores de infraestrutura e hospedagem (para manter o app funcionando)',
          'Processadores de pagamento (apenas dados necessários para cobrança)',
          'Autoridades legais, quando exigido por lei',
        ],
      },
      {
        title: '5. Seus Direitos (LGPD)',
        text: 'Conforme a LGPD, você tem direito a:',
        list: [
          'Acessar seus dados pessoais',
          'Corrigir dados incompletos ou desatualizados',
          'Solicitar a exclusão dos seus dados',
          'Revogar consentimento a qualquer momento',
          'Solicitar portabilidade dos seus dados',
          'Obter informações sobre com quem seus dados são compartilhados',
        ],
      },
      {
        title: '6. Armazenamento e Segurança',
        text: 'Utilizamos medidas técnicas e organizacionais para proteger seus dados, incluindo criptografia, controle de acesso e monitoramento contínuo. Seus dados são armazenados em servidores seguros com conformidade internacional.',
      },
      {
        title: '7. Retenção de Dados',
        text: 'Mantemos seus dados enquanto sua conta estiver ativa. Após exclusão da conta, seus dados serão removidos em até 30 dias, exceto quando a retenção for exigida por lei.',
      },
      {
        title: '8. Contato do Encarregado (DPO)',
        text: 'Para exercer seus direitos ou esclarecer dúvidas sobre privacidade, entre em contato: privacidade@menoease.com',
      },
    ],
  },
  cookies: {
    title: 'Política de Cookies',
    icon: Shield,
    intro: 'Esta política explica como o MenoEase utiliza cookies e tecnologias similares para melhorar sua experiência.',
    sections: [
      {
        title: '1. O que são Cookies?',
        text: 'Cookies são pequenos arquivos de texto armazenados no seu dispositivo quando você acessa nosso site ou aplicativo. Eles nos ajudam a lembrar suas preferências e entender como você utiliza o Serviço.',
      },
      {
        title: '2. Tipos de Cookies que Utilizamos',
        text: 'Cookies Essenciais: necessários para o funcionamento básico do aplicativo, como manter sua sessão ativa e garantir a segurança. Não podem ser desabilitados.\n\nCookies de Preferências: armazenam suas configurações e preferências (como idioma e notificações) para uma experiência personalizada.\n\nCookies de Análise: nos ajudam a entender como o aplicativo é utilizado, quais funcionalidades são mais acessadas e onde podemos melhorar. Esses dados são coletados de forma agregada e anônima.',
      },
      {
        title: '3. Cookies de Terceiros',
        text: 'Podemos utilizar serviços de terceiros que também utilizam cookies:',
        list: [
          'Análise de uso: para entender padrões de navegação e melhorar o app',
          'Processamento de pagamento: para transações seguras',
        ],
      },
      {
        title: '4. Como Gerenciar Cookies',
        text: 'Você pode controlar cookies através das configurações do seu navegador. A maioria dos navegadores permite:',
        list: [
          'Ver quais cookies estão armazenados',
          'Excluir cookies individualmente ou todos de uma vez',
          'Bloquear cookies de terceiros',
          'Bloquear todos os cookies (pode afetar o funcionamento do app)',
        ],
      },
      {
        title: '5. Consentimento',
        text: 'Ao utilizar o MenoEase, você consente com o uso de cookies essenciais. Para cookies de análise e preferências, solicitaremos seu consentimento explícito, que pode ser revogado a qualquer momento.',
      },
      {
        title: '6. Atualizações',
        text: 'Esta política pode ser atualizada periodicamente. Recomendamos revisá-la regularmente.',
      },
      {
        title: '7. Contato',
        text: 'Dúvidas sobre cookies? Entre em contato: contato@menoease.com',
      },
    ],
  },
  sobre: {
    title: 'Sobre Nós',
    icon: Heart,
    type: 'narrative',
    sections: [
      {
        text: 'Existe um momento na vida de muitas mulheres que chega sem aviso, sem manual e, muitas vezes, sem apoio. A menopausa não é apenas uma mudança hormonal — é uma transformação que toca o corpo, a mente, as emoções e a forma como uma mulher se enxerga no mundo.',
      },
      {
        text: 'E mesmo assim, por décadas, esse momento foi tratado com silêncio. Ignorado em conversas familiares, minimizado em consultas médicas, e vivido por milhões de mulheres de forma solitária — como se fosse algo a ser suportado em silêncio, e não compreendido e acolhido.',
      },
      {
        title: 'Por que o MenoEase existe',
        text: 'O MenoEase nasceu de uma constatação simples e poderosa: nenhuma mulher deveria passar pela menopausa sentindo que está sozinha, perdida ou "enlouquecendo". Aquela insônia persistente tem nome. Aquela névoa mental tem explicação. Aquelas ondas de calor no meio da madrugada não são frescura — são sintomas reais de uma fase real da vida.',
      },
      {
        text: 'Nós acreditamos que informação é o primeiro passo para o acolhimento. Quando uma mulher entende o que está acontecendo com o seu corpo, ela retoma o controle. Quando ela consegue rastrear seus sintomas e levar relatórios claros para o seu médico, as consultas deixam de ser frustrantes e passam a ser produtivas. Quando ela tem acesso a conteúdo confiável às 3 da manhã — aquela hora em que o sono não vem — ela sabe que não está sozinha.',
      },
      {
        title: 'Nossa missão',
        text: 'Transformar confusão em clareza. Transformar solidão em acolhimento. Transformar a experiência da menopausa em uma jornada vivida com dignidade, informação e suporte — 24 horas por dia, 7 dias por semana.',
      },
      {
        text: 'O MenoEase é mais do que um aplicativo. É uma companheira digital que foi pensada com empatia, construída com tecnologia e guiada por um único propósito: que toda mulher possa viver essa fase da vida com mais clareza, mais confiança e mais qualidade de vida.',
      },
      {
        title: 'Para quem fazemos isso',
        text: 'Para a mulher de 45 anos que começou a sentir coisas que não consegue explicar. Para a mulher de 52 que foi ao médico e ouviu que "é normal". Para a mulher de 60 que ainda busca respostas. Para todas as mulheres que merecem ser ouvidas, compreendidas e apoiadas — em cada fase da transição.',
      },
      {
        text: 'Se você chegou até aqui, saiba: você não está sozinha. E estamos aqui por você. 💗',
      },
    ],
  },
  contato: {
    title: 'Entre em Contato',
    icon: Mail,
    type: 'contact',
  },
};

function ModalSection({ section }) {
  return (
    <div>
      <h4 className="text-[15px] font-bold text-stone-900 mt-6 mb-2 pt-4 border-t border-[#FAADC7]/15 first:mt-0 first:pt-0 first:border-t-0">
        {section.title}
      </h4>
      {section.text && (
        <p className="text-sm text-stone-500 leading-relaxed mb-3 whitespace-pre-line">
          {section.text}
        </p>
      )}
      {section.list && (
        <ul className="text-sm text-stone-500 leading-relaxed ml-5 mb-4 list-disc space-y-1.5">
          {section.list.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LegalModal({ modalKey, isOpen, onClose }) {
  const content = modalContent[modalKey];
  const Icon = content ? content.icon : null;

  // Close on Escape
  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!content) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-6"
          style={{ background: 'rgba(28, 25, 23, 0.4)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-[680px] max-h-[80vh] flex flex-col overflow-hidden rounded-3xl border border-white/50"
            style={{
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15), inset 0 0 0 1px rgba(255,255,255,0.2)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-7 pt-6 pb-4 border-b border-[#FAADC7]/20 flex-shrink-0">
              <h3 className="text-xl font-bold text-stone-900 flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)' }}>
                <span className="w-9 h-9 rounded-[10px] bg-[#FAADC7]/20 flex items-center justify-center">
                  <Icon size={18} className="text-[#B54182]" />
                </span>
                {content.title}
              </h3>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center text-stone-400 hover:bg-[#B54182]/10 hover:text-[#B54182] transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body with scroll */}
            <div className="px-7 py-6 overflow-y-auto flex-1 legal-modal-scroll">
              {content.type === 'contact' ? (
                <ContactForm onClose={onClose} />
              ) : (
                <>
                  {content.type !== 'narrative' && (
                    <p className="text-xs text-stone-400 italic mb-5">Última atualização: Março de 2026</p>
                  )}
                  {content.intro && (
                    <p className="text-sm text-stone-500 leading-relaxed mb-4">{content.intro}</p>
                  )}
                  {content.sections && content.sections.map((section, i) => (
                    <ModalSection key={i} section={section} />
                  ))}
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ContactForm({ onClose }) {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const response = await fetch('https://formspree.io/f/xqeynwny', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Erro ao enviar. Tente novamente.');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    }
    setSending(false);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-[#FAADC7]/20 flex items-center justify-center mb-6">
          <Heart size={28} className="text-[#B54182]" />
        </div>
        <h4 className="text-lg font-bold text-stone-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
          Mensagem enviada!
        </h4>
        <p className="text-sm text-stone-500 leading-relaxed max-w-sm mb-6">
          Obrigada por entrar em contato. Vamos responder o mais breve possível.
        </p>
        <button
          onClick={onClose}
          className="text-sm font-semibold text-[#B54182] hover:text-[#BE5791] transition-colors"
        >
          Fechar
        </button>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-stone-500 leading-relaxed mb-6">
        Tem alguma dúvida, sugestão ou precisa de ajuda? Adoraríamos ouvir você. Preencha o formulário abaixo e nossa equipe entrará em contato.
      </p>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">Nome</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Seu nome"
            className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white/60 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#FAADC7] focus:ring-2 focus:ring-[#FAADC7]/20 transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">E-mail</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="seu@email.com"
            className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white/60 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#FAADC7] focus:ring-2 focus:ring-[#FAADC7]/20 transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">Assunto</label>
          <input
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Como podemos ajudar?"
            className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white/60 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#FAADC7] focus:ring-2 focus:ring-[#FAADC7]/20 transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-stone-600 mb-1.5">Mensagem</label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Escreva sua mensagem aqui..."
            rows={4}
            className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white/60 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#FAADC7] focus:ring-2 focus:ring-[#FAADC7]/20 transition-all resize-none"
          />
        </div>
        <button
          onClick={handleSubmit}
          disabled={sending}
          className="w-full rounded-full py-3.5 font-semibold text-sm text-white transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#B54182]/25 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #B54182, #BE5791)' }}
        >
          <Send size={16} />
          {sending ? 'Enviando...' : 'Enviar Mensagem'}
        </button>
        {error && (
          <p className="text-xs text-red-500 text-center">{error}</p>
        )}
        <p className="text-xs text-stone-400 text-center">
          Ou envie diretamente para menoease.ai@gmail.com
        </p>
      </div>
    </div>
  );
}

export default function LegalModals({ activeModal, onClose }) {
  return (
    <>
      <LegalModal modalKey="termos" isOpen={activeModal === 'termos'} onClose={onClose} />
      <LegalModal modalKey="privacidade" isOpen={activeModal === 'privacidade'} onClose={onClose} />
      <LegalModal modalKey="cookies" isOpen={activeModal === 'cookies'} onClose={onClose} />
      <LegalModal modalKey="sobre" isOpen={activeModal === 'sobre'} onClose={onClose} />
      <LegalModal modalKey="contato" isOpen={activeModal === 'contato'} onClose={onClose} />
    </>
  );
}
